import { apiClient } from './api.service';
import type { Supplier, SupplierFormData, SupplierFilters, SupplierStats, SupplierIngredient } from '@/types/suppliers.types';

export interface SuppliersResponse {
  success: boolean;
  data: Supplier[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface SupplierResponse {
  success: boolean;
  data: Supplier;
  message?: string;
}

export interface SupplierStatsResponse {
  success: boolean;
  data: SupplierStats;
}

class SuppliersService {
  private readonly basePath = '/suppliers';

  /**
   * Obtener todos los proveedores con filtros
   */
  async getSuppliers(filters?: SupplierFilters & { page?: number; limit?: number }): Promise<SuppliersResponse> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.search) params.append('search', filters.search);
      if (filters.isActive !== 'all') params.append('isActive', filters.isActive);
      if (filters.city && filters.city !== 'all') params.append('city', filters.city);
      if (filters.hasIngredients) params.append('hasIngredients', 'true');
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    const response = await apiClient.get(url);
    return response;
  }

  /**
   * Obtener un proveedor específico
   */
  async getSupplier(id: string): Promise<SupplierResponse> {
    const response = await apiClient.get(`${this.basePath}/${id}`);
    return response;
  }

  /**
   * Crear un nuevo proveedor
   */
  async createSupplier(supplierData: SupplierFormData): Promise<SupplierResponse> {
    const response = await apiClient.post(this.basePath, supplierData);
    return response;
  }

  /**
   * Actualizar un proveedor existente
   */
  async updateSupplier(id: string, supplierData: Partial<SupplierFormData>): Promise<SupplierResponse> {
    const response = await apiClient.put(`${this.basePath}/${id}`, supplierData);
    return response;
  }

  /**
   * Desactivar un proveedor (soft delete)
   */
  async deleteSupplier(id: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete(`${this.basePath}/${id}`);
    return response;
  }

  /**
   * Asociar ingredientes a un proveedor
   */
  async updateSupplierIngredients(id: string, ingredients: SupplierIngredient[]): Promise<SupplierResponse> {
    const response = await apiClient.post(`${this.basePath}/${id}/ingredients`, { ingredients });
    return response;
  }

  /**
   * Desvincular ingrediente de un proveedor (soft delete)
   */
  async removeSupplierIngredient(supplierId: string, ingredientId: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete(`${this.basePath}/${supplierId}/ingredients/${ingredientId}`);
    return response;
  }

  /**
   * Obtener estadísticas de proveedores
   */
  async getSupplierStats(): Promise<SupplierStatsResponse> {
    const response = await apiClient.get(`${this.basePath}/stats`);
    return response;
  }

  /**
   * Obtener ciudades únicas de proveedores
   */
  async getCities(): Promise<string[]> {
    const response = await this.getSuppliers();
    const cities = Array.from(new Set(response.data.map(supplier => supplier.address.city)));
    return cities.sort();
  }


  /**
   * Buscar proveedores por texto
   */
  async searchSuppliers(query: string): Promise<Supplier[]> {
    const response = await this.getSuppliers({
      search: query,
      isActive: 'all',
      city: 'all',
      hasIngredients: false,
      sortBy: 'businessName',
      sortOrder: 'asc',
      limit: 50
    });
    return response.data;
  }

  /**
   * Obtener proveedores activos solamente
   */
  async getActiveSuppliers(): Promise<Supplier[]> {
    const response = await this.getSuppliers({
      isActive: 'active',
      search: '',
      city: 'all',
      hasIngredients: false,
      sortBy: 'businessName',
      sortOrder: 'asc'
    });
    return response.data;
  }

  /**
   * Obtener proveedores por ciudad
   */
  async getSuppliersByCity(city: string): Promise<Supplier[]> {
    const response = await this.getSuppliers({
      city,
      isActive: 'all',
      search: '',
      hasIngredients: false,
      sortBy: 'businessName',
      sortOrder: 'asc'
    });
    return response.data;
  }

  /**
   * Validar RUC único
   */
  async validateRUC(ruc: string, excludeId?: string): Promise<boolean> {
    try {
      const response = await this.getSuppliers({
        search: ruc,
        isActive: 'all',
        city: 'all',
        hasIngredients: false,
        sortBy: 'businessName',
        sortOrder: 'asc'
      });

      const existingSupplier = response.data.find(supplier => 
        supplier.ruc === ruc && supplier.id !== excludeId
      );

      return !existingSupplier; // Retorna true si el RUC es único
    } catch (error) {
      console.error('Error validating RUC:', error);
      return false;
    }
  }

  /**
   * Exportar proveedores a CSV
   */
  generateCSV(suppliers: Supplier[]): string {
    const headers = [
      'ID',
      'Razón Social',
      'RUC',
      'Ciudad',
      'Contacto Principal',
      'Email',
      'Teléfono',
      'Estado',
      'Productos',
      'Total Órdenes',
      'Valor Total',
      'Calificación',
      'Fecha Creación'
    ];

    const rows = suppliers.map(supplier => [
      supplier.id,
      supplier.businessName,
      supplier.ruc,
      supplier.address.city,
      supplier.primaryContact.name,
      supplier.primaryContact.email || '',
      supplier.primaryContact.phone || '',
      supplier.isActive ? 'Activo' : 'Inactivo',
      supplier.ingredients.length.toString(),
      supplier.totalOrders?.toString() || '0',
      supplier.totalValue?.toString() || '0',
      supplier.rating?.toString() || '0',
      new Date(supplier.createdAt).toLocaleDateString('es-PE')
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    return csvContent;
  }

  /**
   * Descargar CSV de proveedores
   */
  downloadCSV(suppliers: Supplier[], filename = 'proveedores.csv'): void {
    const csvContent = this.generateCSV(suppliers);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

export const suppliersService = new SuppliersService();